import { List, Paper, makeStyles } from "@material-ui/core";
import { Business } from "@material-ui/icons";
import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ItemsState } from "../../redux/state";
import { ListItem } from "@ofqual/portal-components/components/List/ListItem";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { SocItemModel } from "../../domain/SocItem";
import {
  useAuth,
  useFieldLookups,
  useLookup,
} from "@ofqual/portal-core/core/hooks";
import { cachingAoService, socStatusService } from "../../services";
import { authSelector } from "../../redux/selectors";
import { UserType } from "../../redux/types";
import { ItemType } from "@ofqual/portal-core/core/hooks/useFieldLookups";

const SEARCH_STATE_KEY = "search";

const useStyles = makeStyles((theme) => ({
  active: {
    ...theme.list.itemPrimary,
  },
}));

export const ItemsList: React.FC<{ state: ItemsState }> = ({ state }) => {
  const classes = useStyles();

  const { response } = state;
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { userType } = useAuth(authSelector);

  const navigate = (id: string) =>
    history.push(`${pathname}/${id}`, { [SEARCH_STATE_KEY]: search });

  const [socStatusLookups] = useLookup(socStatusService);
  const orgIdsPresent = useMemo(
    () =>
      response.data
        .filter((x) => !!x.organisationId)
        .map((item: SocItemModel) => item.organisationId)
        .flat(),
    [response]
  );

  const orgLookups = useFieldLookups(cachingAoService, orgIdsPresent);

  return orgLookups ? (
    <Paper>
      <List disablePadding>
        {response.data.map((item, index) => (
          <React.Fragment key={item.id}>
            <ListItem
              button
              alignItems="flex-start"
              onClick={() => navigate(item.id)}
              divider
              className={classes.active}
              data-test="row"
              Icon={Business}
              primaryText={`${item.referenceNumber} - ${item.caseTitle}`}
              secondaryDetails={
                userType === UserType.Ofqual
                  ? secondaryListOptionsOfqual(item, orgLookups)
                  : secondaryListOptions(item, orgLookups)
              }
              chipTexts={[
                {
                  text: `Status: ${socStatusLookups.find((x) => x.id === item.status)?.label ??
                    "Unknown"
                    }`,
                },
              ]}
            />
          </React.Fragment>
        ))}
      </List>
    </Paper>
  ) : null;
};

const secondaryListOptions = (item: SocItemModel, orgLookups: ItemType) => [
  {
    heading: "AO",
    value: item.organisationId ? orgLookups[item.organisationId] : null,
  },
  {
    heading: "Last response received",
    value: !!item.lastResponseDate
      ? formatUtcDate(item.lastResponseDate, standardDateFormats.DATE)
      : "None",
  },
];

const secondaryListOptionsOfqual = (item: SocItemModel, orgLookups: ItemType) => {
  const secondaryOptions = secondaryListOptions(item, orgLookups);
  return [
    {
      heading: "Assigned to",
      value: item.assignedToDisplayName ?? "Unassigned",
    },
    ...secondaryOptions,
  ];
};